/**
 * Raise Alert feature — Critical Finding Alert Engine.
 *
 * Pure functions only: no React, no network, no DOM writes. Takes report text +
 * rules + lexicon and returns which findings fired and which were suppressed.
 * That keeps it unit-testable and makes moving it server-side a non-event.
 *
 * Pipeline (DeploymentBrief section 4.2), first suppression wins:
 *   L1 history / indication section
 *   L2 already escalated
 *   L3 negation, with double-negative guard  (double negative -> fires, downgraded)
 *   L4 chronicity, with acute override
 *   L5 post-operative context   (only rules with suppression.postOp)
 *   L6 numeric threshold        (no number found -> fires, fail-open)
 *   L7 more-specific wins       (runs last, across the survivors, not per match)
 *
 * Deviations from the brief, deliberate:
 *   - negation is scoped to the SENTENCE, not a fixed 80-character window. An
 *     unrelated "No" 60 chars earlier must not swallow a real finding.
 *   - negation is also checked AFTER the term ("Pneumothorax - not seen").
 *   - post-op suppression is per-rule, not global, so a post-op patient's
 *     stroke is never suppressed.
 */

export const SUPPRESSION_LAYER = {
  HISTORY_SECTION: "L1_HISTORY_SECTION",
  ALREADY_ESCALATED: "L2_ALREADY_ESCALATED",
  NEGATION: "L3_NEGATION",
  CHRONICITY: "L4_CHRONICITY",
  POST_OP: "L5_POST_OP",
  NUMERIC_THRESHOLD: "L6_NUMERIC_THRESHOLD",
  /** A more specific finding matched the same sentence, e.g. tension pneumothorax over pneumothorax. */
  MORE_SPECIFIC: "L7_MORE_SPECIFIC",
  RULE_EXCLUSION: "RULE_EXCLUSION_TERM",
};

const PRIORITY_RANK = { CRITICAL: 0, URGENT: 1, HIGH_CONCERN: 2 };

/* ------------------------------------------------------------------ *
 * 1. Text extraction
 * ------------------------------------------------------------------ */

/**
 * Froala HTML -> plain text.
 *
 * Block-level tags become NEWLINES, not spaces. This matters: sentence-scoped
 * negation depends on it. "<p>No pneumothorax</p><p>Large effusion</p>" must not
 * collapse into one sentence, or the "No" would suppress the effusion.
 */
export const htmlToPlainText = (html) => {
  if (!html || typeof html !== "string") {
    return "";
  }

  let text = html;

  // Froala's free-tier footer, already stripped elsewhere in the app.
  text = text.replace(
    /<p[^>]*>\s*Powered by\s*<a[^>]*>Froala Editor<\/a>\s*<\/p>/gi,
    " "
  );

  text = text.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ");
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(
    /<\/(p|div|li|tr|h[1-6]|table|section|blockquote)\s*>/gi,
    "\n"
  );
  text = text.replace(/<\/(td|th)\s*>/gi, "\t");
  /* INLINE formatting tags leave NOTHING behind, not a space.
   *
   * Reported 22-Sep-2026: bolding one word inside a trigger phrase stopped the alert.
   *   "The loss of <strong>grey</strong>-white differentiation is observed."
   * used to flatten to "loss of grey -white differentiation" -- a space where the
   * closing tag had been -- so the term "loss of grey-white differentiation" no longer
   * matched. Every rule was exposed: bold, italic, underline or a coloured span
   * anywhere inside a trigger phrase silently broke it, and radiologists emphasise
   * exactly the words that matter most.
   *
   * Block-level tags still become a space, because there you DO need a separator or
   * the last word of one paragraph fuses with the first of the next. */
  text = text.replace(
    /<\/?(?:strong|b|em|i|u|s|strike|del|ins|span|mark|font|small|big|sub|sup|a|code|abbr|label)(?:\s[^>]*)?>/gi,
    ""
  );
  text = text.replace(/<[^>]+>/g, " ");

  // entities we actually see in reports
  const entities = {
    "&nbsp;": " ",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&apos;": "'",
    "&ge;": "≥",
    "&le;": "≤",
    "&deg;": "°",
    "&mdash;": "—",
    "&ndash;": "–",
  };
  Object.keys(entities).forEach((key) => {
    text = text.replace(new RegExp(key, "gi"), entities[key]);
  });
  text = text.replace(/&#(\d+);/g, (m, code) =>
    String.fromCharCode(Number(code))
  );

  text = text.replace(/[ \t ]+/g, " ");
  text = text.replace(/\n{2,}/g, "\n");

  return text.trim();
};

/**
 * Collect the scannable text out of a submit payload's studyReportList.
 * Every report's finalReport and addendum is included, separated by hard breaks
 * so no sentence ever spans two reports.
 */
export const extractReportText = (studyReportList) => {
  if (!Array.isArray(studyReportList)) {
    return "";
  }
  return studyReportList
    .flatMap((report) => [report?.finalReport, report?.addendum])
    .filter((html) => html && String(html).trim().length > 0)
    .map((html) => htmlToPlainText(html))
    .filter((txt) => txt.length > 0)
    .join("\n");
};

/* ------------------------------------------------------------------ *
 * 2. Sentence segmentation
 * ------------------------------------------------------------------ */

/**
 * Split into sentences, keeping absolute offsets so matches can be located.
 *
 * Breaks on . ! ? ; : and newlines — radiology uses ; and : as list separators.
 *
 * A full stop BETWEEN TWO DIGITS is a decimal point, not a sentence end. Without
 * that exception "Midline shift of 0.9 cm" splits into "Midline shift of 0" and
 * "9 cm", and the threshold check then reads the measurement as 0 — which
 * silently suppressed a real finding for any decimal value.
 */
export const splitSentences = (text) => {
  const sentences = [];
  if (!text) {
    return sentences;
  }

  const isDigit = (ch) => ch >= "0" && ch <= "9";

  const isBoundary = (i) => {
    const ch = text[i];
    if (ch === "\n" || ch === "!" || ch === "?" || ch === ";" || ch === ":") {
      return true;
    }
    if (ch !== ".") {
      return false;
    }
    // decimal point, e.g. "0.7" or "5.5 cm" — not a sentence end
    return !(isDigit(text[i - 1]) && isDigit(text[i + 1]));
  };

  const add = (start, end) => {
    const raw = text.slice(start, end);
    if (raw.trim().length > 0) {
      sentences.push({
        text: raw,
        lower: raw.toLowerCase(),
        start,
        end,
      });
    }
  };

  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (isBoundary(i)) {
      add(start, i + 1);
      start = i + 1;
    }
  }
  add(start, text.length);

  return sentences;
};

/* ------------------------------------------------------------------ *
 * 3. Modality resolution
 * ------------------------------------------------------------------ */

const normalise = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * Our study data -> engine modality codes. Returns an array; a study can map to
 * more than one (a "CT CHEST ABDOMEN PELVIS" gets both chest and abdomen rules).
 */
export const resolveEngineModalities = (
  { modality, studyDescription, subModalityRegion } = {},
  modalityMap
) => {
  const resolved = new Set();
  const mod = normalise(modality);
  const desc = normalise(studyDescription);
  const entries = modalityMap?.entries || [];

  if (!mod) {
    return [];
  }

  const applicable = entries.filter((entry) =>
    (entry.modality || []).some((m) => normalise(m) === mod)
  );

  applicable.forEach((entry) => {
    if (entry.fallback) {
      return;
    }
    const hit = (entry.descriptionKeywords || []).some((kw) =>
      desc.includes(normalise(kw))
    );
    if (hit) {
      resolved.add(entry.engineModality);
    }
  });

  // a region tag from sub_modality wins outright when we have one
  if (subModalityRegion) {
    const region = normalise(subModalityRegion);
    applicable.forEach((entry) => {
      if (normalise(entry.engineModality).includes(region)) {
        resolved.add(entry.engineModality);
      }
    });
  }

  /* Nothing matched on description. Scan EVERY region this DICOM modality can
   * reach, not just the one flagged `fallback`.
   *
   * Picking one region silently threw away every rule for the real one. A CT
   * described "CT ABD" or "CT TRAUMA" matches no abdomen keyword, fell back to
   * CT_HEAD, and was then scanned with brain rules only -- so a pneumoperitoneum
   * or a volvulus in the report raised nothing at all, with no error and no log.
   * A plain film described "CXR" was worse: "CXR" does not contain the substring
   * "CHEST", so it fell back to XR and missed all 16 chest rules.
   *
   * An unknown region is not evidence of a particular region. Fail open, the way
   * L1 does for an unrecognised report template and L6 does for a missing
   * measurement: scan more and let the suppression layers and the radiologist
   * decide, rather than go quiet on a critical finding. The trigger terms are
   * region-specific enough that cross-firing is rare -- an abdominal report does
   * not contain "midline shift".
   *
   * The `fallback` flag is still honoured: its entry is one of these, and it is
   * the only one for modalities that map to a single region (ECG, PET). */
  if (resolved.size === 0) {
    applicable.forEach((entry) => resolved.add(entry.engineModality));
  }

  return Array.from(resolved);
};

/* ------------------------------------------------------------------ *
 * 4. Matching
 * ------------------------------------------------------------------ */

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Whole-word/phrase matcher. \b fails on terms ending in a non-word char
 * (e.g. "2:1 block"), so boundaries are asserted with lookarounds instead.
 */
const buildTermRegex = (term, caseSensitive) => {
  const escaped = escapeRegExp(term);
  const startsWord = /^[\w]/.test(term);
  const endsWord = /[\w]$/.test(term);
  const prefix = startsWord ? "(?<![\\w])" : "";
  const suffix = endsWord ? "(?:e?s)?(?![\\w])" : "";
  return new RegExp(prefix + escaped + suffix, caseSensitive ? "g" : "gi");
};

/**
 * One entry from trigger_terms.
 *
 * Most are plain strings, matched whole-word and case-insensitively. The ~18
 * short abbreviations carry an object form so they only match in capitals —
 * without that, "pea" in ordinary prose fires the cardiac-arrest rule and "vt"
 * fires ventricular tachycardia.
 *
 *   "pneumothorax"                        -> case-insensitive
 *   { "term": "PTX", "caseSensitive": true } -> capitals only
 */
const normaliseTerm = (entry) => {
  if (typeof entry === "string") {
    return entry.trim() ? { term: entry, caseSensitive: false } : null;
  }
  if (entry && typeof entry === "object" && typeof entry.term === "string" && entry.term.trim()) {
    return {
      term: entry.term,
      caseSensitive: entry.caseSensitive === true,
      /* A measurement LABEL rather than a finding -- "ctr", "cardiomegaly".
       * Layer 6 fails open by default, which is right for a described finding
       * ("severe cardiomegaly" with no number stated is still severe) but wrong
       * for a label: "Mild cardiomegaly." would raise a severe-cardiomegaly
       * alert on a very ordinary report. A term marked this way fails CLOSED --
       * no measurement, no alert. */
      requiresNumber: entry.requiresNumber === true,
      /* NEW 10-Sep-2026: per-term priority override.
       *
       * Priority normally belongs to the rule, but one rule can hold terms of
       * different weight. cxr_severe_cardiomegaly is URGENT because "severe
       * cardiomegaly" is; "enlarged cardiac silhouette" is the same finding
       * described softly and without a measurement, and the clinicians asked for
       * it to be downgraded WITHOUT renaming the finding or splitting the rule.
       *
       * Anything that is not a known priority is ignored, so a typo falls back to
       * the rule's own priority rather than producing an unranked finding (an
       * unknown priority sorts last and gets the fallback badge). */
      priority:
        Object.prototype.hasOwnProperty.call(PRIORITY_RANK, entry.priority)
          ? entry.priority
          : undefined,
    };
  }
  return null;
};

/**
 * The name to show for this finding on THIS study.
 *
 * A few findings are named differently per modality in the clinical checklist --
 * the same rule is one row in the AXR section and another in the CT ABDOMEN
 * section, worded differently. `findingNameByModality` carries those variants,
 * keyed by engine modality, with `findingName` as the default.
 *
 * Only use it where the two names mean the SAME thing. It must never be used to
 * make the alert claim more than the report said: naming a rule
 * "Ruptured ectopic pregnancy" on CT when it fired on the words "tubal ectopic"
 * asserts a rupture nobody wrote. Where the checklist's two names differ in
 * meaning rather than wording, the answer is two rules, not two labels.
 *
 * A study can resolve to several engine modalities at once (an unrecognised CT
 * description reaches all five CT regions), so the rule's own appliesTo order
 * decides which override wins. That keeps the choice deterministic and in the
 * hands of whoever wrote the rule.
 */
const resolveFindingName = (rule, engineModalities) => {
  const overrides = rule.findingNameByModality;
  if (!overrides || typeof overrides !== "object") {
    return rule.findingName;
  }
  const declared = (rule.appliesTo || []).flatMap(
    (block) => block.engineModality || []
  );
  for (let i = 0; i < declared.length; i++) {
    const m = declared[i];
    if (overrides[m] && (engineModalities || []).includes(m)) {
      return overrides[m];
    }
  }
  return rule.findingName;
};

/* Exported so the manual "Search a finding" picker can filter by the same test the
 * scan uses. Duplicating it there would let the two drift, and a picker that offers
 * an alert the scan would never raise for this study is worse than no filter. */
export const ruleAppliesToModalities = (rule, engineModalities, studyContext) => {
  const blocks = rule.appliesTo || [];
  if (blocks.length === 0) {
    return true;
  }
  const desc = normalise(studyContext?.studyDescription);
  const mod = normalise(studyContext?.modality);

  return blocks.some((block) => {
    // engine-modality form (how the seeded rules are written)
    if (Array.isArray(block.engineModality)) {
      if (block.engineModality.includes("ALL")) {
        return true;
      }
      if (block.engineModality.some((m) => engineModalities.includes(m))) {
        return true;
      }
    }
    // raw DICOM + description form (what the DB column will hold)
    if (Array.isArray(block.modality)) {
      const modalityHit = block.modality.some((m) => normalise(m) === mod);
      if (!modalityHit) {
        return false;
      }
      const keywords = block.studyDescription || [];
      if (keywords.length === 0) {
        return true;
      }
      return keywords.some((kw) => desc.includes(normalise(kw)));
    }
    return false;
  });
};

/** Stage 1 — dumb term hunt. Returns one entry per (rule, term) hit. */
export const matchRules = (text, rules, engineModalities, studyContext) => {
  const matches = [];
  if (!text) {
    return matches;
  }
  const sentences = splitSentences(text);

  const sentenceAt = (index) =>
    sentences.find((s) => index >= s.start && index < s.end) || {
      text: text.slice(Math.max(0, index - 80), index + 120),
      lower: "",
      start: 0,
      end: 0,
    };

  (rules || []).forEach((rule) => {
    if (rule.active === false) {
      return;
    }
    if (!ruleAppliesToModalities(rule, engineModalities, studyContext)) {
      return;
    }

    const terms = (rule.triggerTerms || []).map(normaliseTerm).filter(Boolean);

    terms.forEach(({ term, caseSensitive, requiresNumber, priority: termPriority }) => {
      if (!term) {
        return;
      }
      const re = buildTermRegex(term, caseSensitive);
      let hit;
      while ((hit = re.exec(text)) !== null) {
        const sentence = sentenceAt(hit.index);
        /* A term inside a section label is judged on the answer beneath it --
         * see answerOpensWithNegation. */
        let labelAnswerLower = "";
        let labelAnswer = "";
        if (/:\s*$/.test(sentence.text || "")) {
          const at = sentences.indexOf(sentence);
          const next = at >= 0 ? sentences[at + 1] : null;
          if (next) {
            labelAnswerLower = next.lower || String(next.text || "").toLowerCase();
            labelAnswer = String(next.text || "").trim();
          }
        }
        matches.push({
          labelAnswerLower,
          labelAnswer,
          alertCode: rule.alertCode,
          findingName: resolveFindingName(rule, engineModalities),
          // a term may carry its own priority; the rule's is the default
          priority: termPriority || rule.priority,
          rule,
          matchedTerm: hit[0],
          requiresNumber: requiresNumber === true,
          matchIndex: hit.index,
          sentence: sentence.text.trim(),
          sentenceLower: sentence.text.toLowerCase(),
          textBefore: text.slice(0, hit.index).toLowerCase(),
        });
        if (hit.index === re.lastIndex) {
          re.lastIndex++; // guard against zero-length loops
        }
      }
    });
  });

  return matches;
};

/* ------------------------------------------------------------------ *
 * 5. Suppression pipeline
 * ------------------------------------------------------------------ */

const phrasesOf = (lexicon, key) => lexicon?.[key]?.phrases || [];

const containsPhrase = (haystackLower, phrases) =>
  phrases.some((phrase) => {
    const p = String(phrase).toLowerCase();
    if (!p) {
      return false;
    }
    if (/^[\w]/.test(p) && /[\w]$/.test(p)) {
      return new RegExp(`(?<![\\w])${escapeRegExp(p)}(?![\\w])`).test(
        haystackLower
      );
    }
    return haystackLower.includes(p);
  });

/** Which phrase hit — used for the suppression log. */
const firstPhrase = (haystackLower, phrases) =>
  phrases.find((phrase) => containsPhrase(haystackLower, [phrase])) || null;

/* ------------------------------------------------------------------ *
 * Normality assertions  --  "is normal", "appear normal", "grossly unremarkable"
 *
 * NEW 10-Sep-2026, from MRI spine feedback: alerts were being raised on lines that
 * say the structure is FINE:
 *   "Cauda equina compression is normal."
 *   "Evidence of cauda equina syndrome appear normal."
 *
 * These are not negations of the finding -- they assert that the thing named just
 * before them is normal -- so they must only suppress when they attach to the
 * MATCHED TERM. Matching them anywhere in the sentence, the way NEGATION_AFTER
 * does, would be dangerous:
 *
 *   "Pneumothorax present, mediastinum is normal."
 *
 * There, "is normal" describes the mediastinum, and suppressing on it would drop a
 * real pneumothorax. So the phrase has to begin within NORMALITY_MAX_WORD_GAP words
 * of the term -- 1 by default, which admits "<term> is normal" and "<term> is
 * grossly unremarkable" but not the mediastinum above.
 *
 * A negator inside that gap flips the meaning back to an assertion, so it must NOT
 * suppress: "does not appear normal" contains "appear normal". ("is not normal"
 * needs no guard -- it does not contain "is normal" at all.)
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Section labels  --  "FREE INTRAPERITONEAL AIR: No gross free air is evident."
 *
 * NEW 10-Sep-2026. splitSentences ends a sentence at ':', so a templated report puts
 * the LABEL in a sentence of its own:
 *
 *   [0] "FREE INTRAPERITONEAL AIR:"                      <- the template's question
 *   [1] " No gross intraperitoneal free air is evident."  <- the radiologist's answer
 *
 * A trigger term inside the label therefore sits in a sentence with no negation in it
 * and fires, even though the answer right underneath says the opposite. This raised a
 * CRITICAL pneumoperitoneum on a report that explicitly ruled it out. (Same root cause
 * as the long-standing "Pneumothorax: none" false positive.)
 *
 * A label match is judged on its ANSWER instead. The negation has to be the FIRST thing
 * in that answer, which is how a label is actually answered - "No gross ...", "None",
 * "Not seen", "Nil", "There is no ...". A negation deeper in the answer usually belongs
 * to something else, and suppressing on it would hide a real finding:
 *
 *   "FREE INTRAPERITONEAL AIR: Present, with no obstruction."   <- must still fire
 *
 * No answer at all (label at the end of the report) leaves the match alone - fail open.
 * ------------------------------------------------------------------ */

/** The negation phrase the answer OPENS with, or null. */
const answerOpensWithNegation = (answerLower, phraseLists) => {
  const a = String(answerLower || "").replace(/^[\s:;,.\-]+/, "");
  if (!a) {
    return null;
  }
  for (const phrases of phraseLists) {
    for (const phrase of phrases) {
      const p = String(phrase).toLowerCase();
      if (p && a.startsWith(p) && !/[\w]/.test(a.charAt(p.length))) {
        return phrase;
      }
    }
  }
  return null;
};

const NORMALITY_MAX_WORD_GAP = 1;

const NEGATOR_IN_GAP = /\bnot\b|\bnever\b|\bnor\b|\bno\b|\bwithout\b|\bcannot\b|n't/;

/**
 * The normality phrase that attaches to the matched term, or null.
 * `afterLower` is the clause-narrowed text following the term.
 */
const normalityPhraseFor = (afterLower, phrases, maxWordGap) => {
  const gapLimit = typeof maxWordGap === "number" ? maxWordGap : NORMALITY_MAX_WORD_GAP;
  for (const phrase of phrases) {
    const p = String(phrase).toLowerCase();
    if (!p) {
      continue;
    }
    let from = 0;
    for (;;) {
      const at = afterLower.indexOf(p, from);
      if (at === -1) {
        break;
      }
      const gap = afterLower.slice(0, at);
      // whole-word match, the same rule containsPhrase applies
      const boundedLeft = !/[\w]$/.test(gap);
      const boundedRight = !/[\w]/.test(afterLower.charAt(at + p.length));
      if (boundedLeft && boundedRight) {
        const gapWords = gap.split(/[^a-z0-9']+/i).filter(Boolean);
        if (gapWords.length <= gapLimit && !NEGATOR_IN_GAP.test(gap)) {
          return phrase;
        }
      }
      from = at + 1;
    }
  }
  return null;
};

/**
 * Contrast conjunctions — where one clause stops and an opposing one begins.
 *
 * Negation is scoped to the sentence, which is wrong when a sentence negates one
 * finding and then asserts another:
 *
 *   "No effusion but a large pneumothorax is present."
 *   "Ribs are not fractured but there is a large pneumothorax."
 *
 * Sentence-scoped, the "no"/"are not" sits in the pneumothorax's BEFORE text and
 * suppresses a real critical finding. Narrowing to the clause fixes it: nothing
 * on the far side of a "but" can negate this term.
 *
 * Hardcoded rather than held in critical_alert_lexicon, unlike the negation
 * phrases. These are English grammar, not clinical vocabulary — there is no
 * reason for operations to tune them per firm, and keeping them here means the
 * fix needs no migration to take effect.
 */
const CONTRAST_CONJUNCTIONS = [
  "but",
  "however",
  "although",
  "though",
  "whereas",
  "nevertheless",
  "nonetheless",
];

const conjunctionRegex = new RegExp(
  `(?<![\\w])(?:${CONTRAST_CONJUNCTIONS.join("|")})(?![\\w])`,
  "g"
);

/** Keep only the clause the term belongs to — the text after the LAST conjunction. */
const narrowBefore = (text) => {
  if (!text) {
    return text;
  }
  let end = 0;
  let hit;
  conjunctionRegex.lastIndex = 0;
  while ((hit = conjunctionRegex.exec(text)) !== null) {
    end = hit.index + hit[0].length;
  }
  return end > 0 ? text.slice(end) : text;
};

/** Keep only the clause the term belongs to — the text before the FIRST conjunction. */
const narrowAfter = (text) => {
  if (!text) {
    return text;
  }
  conjunctionRegex.lastIndex = 0;
  const hit = conjunctionRegex.exec(text);
  return hit ? text.slice(0, hit.index) : text;
};


/**
 * Every section-header position in the text, so Layer 1 can be decided per match
 * instead of from a single boundary.
 *
 * A single boundary is wrong as soon as the text is more than one report. The
 * scan concatenates every open template, and a "CLINICAL HISTORY:" heading in
 * the SECOND template sat before its own "FINDINGS:" heading, which put the
 * boundary late in the combined text -- so every finding written in the FIRST
 * template fell before it and was suppressed as history. Submitting from that
 * first tab appeared to work only because the live editor appended its text
 * again at the end, past the boundary.
 */
const buildSectionIndex = (text, lexicon) => {
  const lower = (text || "").toLowerCase();
  const headers = lexicon?.SECTION_HEADER || {};
  const collect = (list) => {
    const out = [];
    (list || []).forEach((h) => {
      const needle = String(h).toLowerCase();
      if (!needle) {
        return;
      }
      let from = 0;
      let at = lower.indexOf(needle, from);
      while (at !== -1) {
        out.push(at);
        from = at + needle.length;
        at = lower.indexOf(needle, from);
      }
    });
    return out.sort((a, b) => a - b);
  };
  return {
    history: collect(headers.historyHeaders),
    findings: collect(headers.findingsHeaders),
  };
};

/** Greatest index in `sorted` that is strictly less than `pos`, or -1. */
const lastBefore = (sorted, pos) => {
  let best = -1;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i] < pos) {
      best = sorted[i];
    } else {
      break;
    }
  }
  return best;
};

/**
 * Is this match inside a history / indication section?
 *
 * Decided by looking BACKWARDS from the match at the nearest section heading.
 * A history heading nearer than any findings heading means we are inside the
 * history block. No heading before the match at all means the text is not
 * sectioned there, so it is scanned -- an unfamiliar layout must never be
 * wholly suppressed, and a finding written in an earlier report must not be
 * silenced by a later report's headings.
 */
const isInsideHistorySection = (sectionIndex, matchIndex) => {
  if (!sectionIndex) {
    return false;
  }
  const lastHistory = lastBefore(sectionIndex.history, matchIndex);
  if (lastHistory === -1) {
    return false;
  }
  return lastHistory > lastBefore(sectionIndex.findings, matchIndex);
};

/** Split "6-8 mm" style ranges and pick the upper value, per the brief. */
const extractNumberNear = (sentence, thresholdUnit) => {
  if (!sentence) {
    return null;
  }
  const lower = sentence.toLowerCase();

  const unitPattern =
    thresholdUnit === "mm"
      ? "(mm|cm|millimet(?:er|re)s?|centimet(?:er|re)s?)"
      : thresholdUnit === "ms"
      ? "(ms|msec|millisec(?:ond)?s?)"
      : thresholdUnit === "bpm"
      ? "(bpm|beats\\s*(?:per|/)\\s*min(?:ute)?)"
      : null;

  const numberToken = "(\\d+(?:\\.\\d+)?)(?:\\s*(?:-|–|to)\\s*(\\d+(?:\\.\\d+)?))?";
  const re = unitPattern
    ? new RegExp(numberToken + "\\s*" + unitPattern, "g")
    : new RegExp(numberToken, "g");

  let best = null;
  let hit;
  while ((hit = re.exec(lower)) !== null) {
    const low = parseFloat(hit[1]);
    const high = hit[2] !== undefined ? parseFloat(hit[2]) : null;
    let value = high !== null ? Math.max(low, high) : low; // range -> upper bound
    const unit = unitPattern ? hit[3] : null;

    if (thresholdUnit === "mm" && unit && /^c/.test(unit)) {
      value *= 10; // 0.9 cm -> 9 mm
    }
    /* A "ratio" threshold has no unit to anchor on, so without this any number
     * in the sentence is read as the ratio: "Cardiomegaly with 3 vessel disease"
     * would compare 3 against 0.55 and fire. A cardiothoracic ratio is between
     * 0 and 1, so anything from 2 upwards is some other measurement. */
    if (thresholdUnit === "ratio" && value >= 2) {
      continue;
    }
    if (best === null || value > best) {
      best = value;
    }
  }
  return best;
};

/**
 * Stage 2 — run every match through the six layers.
 * Returns { fired, suppressed }. `fired` is deduplicated per alertCode.
 */
export const applySuppression = (matches, lexicon, options = {}) => {
  const { fullText = "", enabledLayers = null } = options;

  const layerOn = (layer) => !enabledLayers || enabledLayers.includes(layer);

  const fullTextLower = (fullText || "").toLowerCase();

  const sectionIndex = layerOn(SUPPRESSION_LAYER.HISTORY_SECTION)
    ? buildSectionIndex(fullText, lexicon)
    : null;

  const escalatedPhrases = phrasesOf(lexicon, "ALREADY_ESCALATED");
  const negBefore = phrasesOf(lexicon, "NEGATION");
  const negAfter = phrasesOf(lexicon, "NEGATION_AFTER");
  const doubleNeg = phrasesOf(lexicon, "DOUBLE_NEGATIVE");
  const normalityAfter = phrasesOf(lexicon, "NORMALITY_AFTER");
  const normalityGap = lexicon?.NORMALITY_AFTER?.maxWordGap;
  const uncertainty = phrasesOf(lexicon, "UNCERTAINTY");
  const chronic = phrasesOf(lexicon, "CHRONICITY");
  const acute = phrasesOf(lexicon, "ACUTE_OVERRIDE");
  const postOp = phrasesOf(lexicon, "POST_OP");

  const suppressed = [];
  const firedByCode = new Map();

  matches.forEach((match) => {
    const rule = match.rule || {};
    const sentenceLower = match.sentenceLower || "";

    // split the sentence at the matched term so BEFORE/AFTER are meaningful
    const termPos = sentenceLower.indexOf(
      String(match.matchedTerm || "").toLowerCase()
    );
    const before = narrowBefore(termPos > 0 ? sentenceLower.slice(0, termPos) : "");
    const after = narrowAfter(
      termPos !== -1
        ? sentenceLower.slice(termPos + String(match.matchedTerm).length)
        : sentenceLower
    );

    const drop = (layer, phrase) =>
      suppressed.push({
        alertCode: match.alertCode,
        findingName: match.findingName,
        matchedTerm: match.matchedTerm,
        sentence: match.sentence,
        layer,
        phrase: phrase || null,
      });

    /* Everything that can stop this rule firing lives in one column, because
     * it is all one question. Three shapes, all optional:
     *   exclude          phrases specific to this rule
     *   postOp           opt IN to the shared POST_OP list
     *   skipChronicity   opt OUT of the shared CHRONICITY list
     * Note the opposite polarity on the last one — it is why this could not be
     * folded into `exclude`. See L4 below. */
    const suppression = rule.suppression || {};

    /* -- rule-specific exclusion terms (Layer 5 done properly) ------- */
    const exclusions = suppression.exclude || [];
    if (exclusions.length > 0 && containsPhrase(sentenceLower, exclusions)) {
      drop(
        SUPPRESSION_LAYER.RULE_EXCLUSION,
        firstPhrase(sentenceLower, exclusions)
      );
      return;
    }

    /* -- L1 history / indication ------------------------------------ */
    if (
      layerOn(SUPPRESSION_LAYER.HISTORY_SECTION) &&
      isInsideHistorySection(sectionIndex, match.matchIndex)
    ) {
      drop(SUPPRESSION_LAYER.HISTORY_SECTION, "history section");
      return;
    }

    /* -- L2 already escalated --------------------------------------- *
     * Report-level by default, per the brief: "the system loop is already
     * closed". The radiologist usually documents the call in its own sentence
     * ("Findings discussed with Dr Sharma"), so a sentence-scoped check would
     * miss it. Set lexicon.ALREADY_ESCALATED.scope = "SENTENCE" to narrow it
     * if this proves to over-suppress in shadow mode.                        */
    if (layerOn(SUPPRESSION_LAYER.ALREADY_ESCALATED)) {
      const escalatedScope = lexicon?.ALREADY_ESCALATED?.scope || "REPORT";
      const haystack =
        escalatedScope === "SENTENCE" ? sentenceLower : fullTextLower;
      if (containsPhrase(haystack, escalatedPhrases)) {
        drop(
          SUPPRESSION_LAYER.ALREADY_ESCALATED,
          firstPhrase(haystack, escalatedPhrases)
        );
        return;
      }
    }

    /* -- L3 negation, double-negative guard first -------------------- */
    const hasDoubleNegative = containsPhrase(sentenceLower, doubleNeg);
    if (layerOn(SUPPRESSION_LAYER.NEGATION) && !hasDoubleNegative) {
      if (containsPhrase(before, negBefore)) {
        drop(SUPPRESSION_LAYER.NEGATION, firstPhrase(before, negBefore));
        return;
      }
      if (containsPhrase(after, negAfter)) {
        drop(SUPPRESSION_LAYER.NEGATION, firstPhrase(after, negAfter));
        return;
      }
      // "<term> is normal" / "<term> appear normal" -- see normalityPhraseFor
      const normality = normalityPhraseFor(after, normalityAfter, normalityGap);
      if (normality) {
        drop(SUPPRESSION_LAYER.NEGATION, normality);
        return;
      }
      /* the term was in a section label; the answer beneath it decides --
       * see answerOpensWithNegation */
      const answerNeg = answerOpensWithNegation(match.labelAnswerLower, [negBefore, negAfter]);
      if (answerNeg) {
        drop(SUPPRESSION_LAYER.NEGATION, answerNeg);
        return;
      }
    }

    /* -- L4 chronicity, acute override ------------------------------ *
     * skipChronicity is an opt-OUT, for findings that ARE chronic by nature:
     * "large bilateral CHRONIC subdural hygromas" is the standard wording for a
     * real finding that needs a call, so the shared CHRONICITY list must not
     * touch it. Putting "chronic" in `exclude` would do the exact reverse.   */
    if (
      layerOn(SUPPRESSION_LAYER.CHRONICITY) &&
      suppression.skipChronicity !== true &&
      containsPhrase(sentenceLower, chronic) &&
      !containsPhrase(sentenceLower, acute)
    ) {
      drop(SUPPRESSION_LAYER.CHRONICITY, firstPhrase(sentenceLower, chronic));
      return;
    }

    /* -- L5 post-operative context (opt-in per rule) ---------------- */
    if (
      layerOn(SUPPRESSION_LAYER.POST_OP) &&
      suppression.postOp === true &&
      containsPhrase(sentenceLower, postOp)
    ) {
      drop(SUPPRESSION_LAYER.POST_OP, firstPhrase(sentenceLower, postOp));
      return;
    }

    /* -- L6 numeric threshold --------------------------------------- */
    let thresholdNote = null;
    if (
      layerOn(SUPPRESSION_LAYER.NUMERIC_THRESHOLD) &&
      typeof rule.numericThreshold === "number"
    ) {
      const value = extractNumberNear(match.sentence, rule.thresholdUnit);
      if (value !== null && value < rule.numericThreshold) {
        drop(
          SUPPRESSION_LAYER.NUMERIC_THRESHOLD,
          `${value} < ${rule.numericThreshold}${rule.thresholdUnit || ""}`
        );
        return;
      }
      if (value === null) {
        if (match.requiresNumber === true) {
          drop(SUPPRESSION_LAYER.NUMERIC_THRESHOLD,
            "measurement-label term with no measurement");
          return;
        }
        // fail open: no measurement written is not evidence of a small finding
        thresholdNote = "no measurement stated";
      }
    }

    /* -- survived: downgrade if hedged ------------------------------ */
    let priority = match.priority;
    let downgradeReason = null;
    if (hasDoubleNegative) {
      downgradeReason = firstPhrase(sentenceLower, doubleNeg);
    } else if (containsPhrase(sentenceLower, uncertainty)) {
      downgradeReason = firstPhrase(sentenceLower, uncertainty);
    }
    if (downgradeReason && priority === "CRITICAL") {
      priority = "URGENT";
    }

    const finding = {
      alertCode: match.alertCode,
      findingName: match.findingName,
      priority,
      originalPriority: match.priority,
      downgradeReason,
      thresholdNote,
      matchedTerm: match.matchedTerm,
      matchedSentence: match.sentence,
      reviewStatus: rule.reviewStatus || null,
      sourceRef: rule.sourceRef || null,
    };

    /* One card per finding, but EVERY sentence that produced it is kept.
     *
     * A radiologist who writes "ptx is found." and later "PTX is seen." has
     * written one pneumothorax, so a second card would be wrong. Showing only
     * the first sentence was also wrong: the card then points at one line of a
     * report that argues the finding in several places, and the reader cannot
     * tell whether the engine saw the rest.
     *
     * `occurrences` holds them all, in document order. `matchedTerm` and
     * `matchedSentence` stay on the finding as the representative pair -- the
     * most severe instance -- so every existing reader keeps working unchanged.
     */
    const occurrence = {
      matchedTerm: match.matchedTerm,
      matchedSentence: match.sentence,
      matchIndex: match.matchIndex,
      // set only when the term matched inside a section label -- see tidyLabelEvidence
      labelAnswer: match.labelAnswer || "",
    };

    const existing = firedByCode.get(finding.alertCode);
    if (!existing) {
      finding.occurrences = [occurrence];
      firedByCode.set(finding.alertCode, finding);
      return;
    }

    /* One line of evidence per SENTENCE, not per term.
     *
     * "Evidence of Sigmoid volvulus is seen." matches this rule twice -- once on
     * "sigmoid volvulus" and once on "volvulus" inside it -- and listing the
     * same sentence twice under one finding reads as though the engine found two
     * separate things. Keep the longer term, because it is the more specific
     * description of what was actually written, and it is what gets highlighted
     * in the modal. */
    const sameSentence = existing.occurrences.find(
      (o) => o.matchedSentence === occurrence.matchedSentence
    );
    if (!sameSentence) {
      existing.occurrences.push(occurrence);
      existing.occurrences.sort((a, b) => a.matchIndex - b.matchIndex);
    } else if (
      String(occurrence.matchedTerm).length >
      String(sameSentence.matchedTerm).length
    ) {
      /* Keep the representative pair in step with the evidence. Without this the
       * finding still reported the shorter term while the card highlighted the longer
       * one -- and it is the representative that is persisted to
       * study_critical_alert.matched_term, so the audit record was the vaguer of the
       * two. ("intraperitoneal free air" vs its sub-phrase "free air".) */
      if (existing.matchedSentence === sameSentence.matchedSentence) {
        existing.matchedTerm = occurrence.matchedTerm;
      }
      sameSentence.matchedTerm = occurrence.matchedTerm;
      sameSentence.matchIndex = occurrence.matchIndex;
    }

    // a more severe instance takes over the representative fields, keeping the
    // evidence gathered so far
    if (
      (PRIORITY_RANK[finding.priority] ?? 9) <
      (PRIORITY_RANK[existing.priority] ?? 9)
    ) {
      existing.priority = finding.priority;
      existing.originalPriority = finding.originalPriority;
      existing.downgradeReason = finding.downgradeReason;
      existing.thresholdNote = finding.thresholdNote;
      existing.matchedTerm = finding.matchedTerm;
      existing.matchedSentence = finding.matchedSentence;
    }
  });

  /* -- L7 more-specific finding wins ------------------------------------ *
   * "Tension pneumothorax is found." legitimately matches two rules: the term
   * "tension pneumothorax" AND the term "pneumothorax" inside it. Both are real
   * matches, but clinically it is ONE finding, and the radiologist should not
   * have to untick the vaguer half of it.
   *
   * So when two findings hit the SAME sentence and one's matched term is a
   * strict sub-phrase of the other's, the broader one is dropped.
   *
   * Guarded by priority, and that guard is the important part. The rule library
   * has one pair where the more specific term is the MILDER one — "bilateral
   * chronic sdh enlarging" (URGENT) contains "sdh" (CRITICAL). Dropping the
   * generic there would silently downgrade an intracranial haemorrhage, so when
   * the specific finding is less severe both are kept and the radiologist
   * decides.                                                                  */
  const supersede = () => {
    const list = Array.from(firedByCode.values());
    const isSubPhrase = (inner, outer) => {
      const i = String(inner || "").toLowerCase();
      const o = String(outer || "").toLowerCase();
      return i !== o && ` ${o} `.includes(` ${i} `);
    };

    /* Superseding is decided per SENTENCE, not per finding.
     *
     * "ptx is found. Tension pneumothorax is seen." gives the pneumothorax rule
     * two sentences. Only the second is spoken for by the tension rule; the
     * first is independent evidence of a plain pneumothorax. Dropping the whole
     * finding because one of its sentences lost would throw that away, so only
     * the losing occurrence goes, and the finding survives on what is left. */
    list.forEach((generic) => {
      const beatenBy = new Map(); // occurrence -> the term that beat it

      generic.occurrences.forEach((occ) => {
        const winner = list.find(
          (specific) =>
            specific !== generic &&
            (PRIORITY_RANK[specific.priority] ?? 9) <=
              (PRIORITY_RANK[generic.priority] ?? 9) &&
            specific.occurrences.some(
              (so) =>
                so.matchedSentence === occ.matchedSentence &&
                isSubPhrase(occ.matchedTerm, so.matchedTerm)
            )
        );
        if (winner) {
          const so = winner.occurrences.find(
            (x) =>
              x.matchedSentence === occ.matchedSentence &&
              isSubPhrase(occ.matchedTerm, x.matchedTerm)
          );
          beatenBy.set(occ, so.matchedTerm);
        }
      });

      if (beatenBy.size === 0) {
        return;
      }

      beatenBy.forEach((phrase, occ) => {
        suppressed.push({
          alertCode: generic.alertCode,
          findingName: generic.findingName,
          matchedTerm: occ.matchedTerm,
          sentence: occ.matchedSentence,
          layer: SUPPRESSION_LAYER.MORE_SPECIFIC,
          phrase,
        });
      });

      const survivors = generic.occurrences.filter((o) => !beatenBy.has(o));
      if (survivors.length === 0) {
        firedByCode.delete(generic.alertCode);
        return;
      }
      generic.occurrences = survivors;
      // the representative pair must still point at surviving evidence
      if (!survivors.some((o) => o.matchedSentence === generic.matchedSentence)) {
        generic.matchedTerm = survivors[0].matchedTerm;
        generic.matchedSentence = survivors[0].matchedSentence;
      }
    });
  };
  supersede();

  /* -- evidence tidy-up: a section label is not evidence ---------------- *
   * A templated report splits into the label and its answer:
   *
   *   "FREE INTRAPERITONEAL AIR:"                              <- matches the term
   *   "gross intraperitoneal free air is evident on this view." <- matches it too
   *
   * Both are real matches on the same finding, so the card listed FOUR lines for
   * two statements, and the bare label came first in document order and became the
   * headline. A line reading only "FREE INTRAPERITONEAL AIR:" tells the radiologist
   * nothing -- the sentence underneath is the evidence.
   *
   * So a label occurrence is dropped whenever the finding also has a real sentence.
   * When the label is the ONLY evidence -- "FREE INTRAPERITONEAL AIR: Present.",
   * where the answer carries no trigger term of its own -- it is kept and shown
   * WITH its answer, because the label alone would be unreadable.                */
  const tidyLabelEvidence = () => {
    const isLabelOnly = (o) => /:\s*$/.test(String(o.matchedSentence || "").trim());

    Array.from(firedByCode.values()).forEach((finding) => {
      const withContent = finding.occurrences.filter((o) => !isLabelOnly(o));

      if (withContent.length > 0) {
        if (withContent.length !== finding.occurrences.length) {
          finding.occurrences = withContent;
          // the representative pair must still point at surviving evidence
          if (!withContent.some((o) => o.matchedSentence === finding.matchedSentence)) {
            finding.matchedTerm = withContent[0].matchedTerm;
            finding.matchedSentence = withContent[0].matchedSentence;
          }
        }
        return;
      }

      // label-only evidence: make it readable by appending the answer
      finding.occurrences.forEach((o) => {
        if (o.labelAnswer) {
          const joined = `${String(o.matchedSentence).trim()} ${o.labelAnswer}`.trim();
          if (finding.matchedSentence === o.matchedSentence) {
            finding.matchedSentence = joined;
          }
          o.matchedSentence = joined;
        }
      });
    });
  };
  tidyLabelEvidence();

  const fired = Array.from(firedByCode.values()).sort(
    (a, b) =>
      (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
      a.findingName.localeCompare(b.findingName)
  );

  return { fired, suppressed };
};

/* ------------------------------------------------------------------ *
 * 6. Public entry point
 * ------------------------------------------------------------------ */

/**
 * Scan a report.
 *
 * @param {object}   input
 * @param {string}   [input.html]              report HTML (Froala)
 * @param {string}   [input.text]              plain text, if already extracted
 * @param {array}    [input.studyReportList]   submit payload form
 * @param {string}   input.modality            DICOM modality (CR, DX, CT, ECG…)
 * @param {string}   [input.studyDescription]
 * @param {string}   [input.subModalityRegion]
 * @param {object}   config  { rules, lexicon, modalityMap, enabledLayers }
 * @returns {{fired:array, suppressed:array, engineModalities:array, scanMs:number, textLength:number}}
 */
export const scanReport = (input = {}, config = {}) => {
  const startedAt =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();

  const { rules = [], lexicon = {}, modalityMap = {}, enabledLayers = null } =
    config;

  let text = input.text || "";
  if (!text && Array.isArray(input.studyReportList)) {
    text = extractReportText(input.studyReportList);
  }
  if (!text && input.html) {
    text = htmlToPlainText(input.html);
  }

  const engineModalities = resolveEngineModalities(input, modalityMap);

  const empty = {
    fired: [],
    suppressed: [],
    engineModalities,
    textLength: text.length,
    scanMs: 0,
  };

  if (!text || engineModalities.length === 0) {
    return empty;
  }

  const matches = matchRules(text, rules, engineModalities, input);
  const { fired, suppressed } = applySuppression(matches, lexicon, {
    fullText: text,
    enabledLayers,
  });

  const endedAt =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();

  return {
    fired,
    suppressed,
    engineModalities,
    textLength: text.length,
    scanMs: Math.round((endedAt - startedAt) * 100) / 100,
  };
};

/* Named exports only. The individual functions are exported rather than kept
   private because verifyCriticalAlertEngine.mjs tests them directly. */